import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from 'react-router';
import { api } from '~/lib/api-client';
import { Button } from '~/components/ui/button';
import { Switch } from '~/components/ui/switch';
import { Skeleton } from '~/components/ui/skeleton';
import { formatDateTime } from '~/lib/local-datetime';
import { settingsPath } from '~/lib/settings-sections';

/**
 * FRONT-1827: the brand kit control at the top of an app's Branding settings.
 * A new app EDDY builds takes its owner's brand kit at its first publish and
 * keeps it; here the owner sees which kit it wears, switches it off (EDDY's
 * house look) or on, and takes the current kit after changing it. Owner only:
 * the server answers anyone else as if the app did not exist.
 */

export interface BrandKitSummary {
  name: string | null;
  source: 'workspace' | 'personal';
  accent: string | null;
  display: string | null;
  body: string | null;
  hasLogo: boolean;
}

export interface AppBrandKitView {
  state: 'on' | 'off' | 'pending' | 'none';
  at: string | null;
  taken: BrandKitSummary | null;
  current: BrandKitSummary | null;
  available: boolean;
  changedSince: boolean;
  /** The next publish takes the current kit (asked in the building session); the app wears `taken` until then. */
  refresh: boolean;
  published: boolean;
}

export const brandKitQueryKey = (slug: string) => ['admin-app', slug, 'brand-kit'] as const;

function kitName(kit: BrandKitSummary): string {
  const source = kit.source === 'personal' ? 'your own kit' : 'workspace kit';
  return kit.name ? `${kit.name} (${source})` : source.charAt(0).toUpperCase() + source.slice(1);
}

function takenDate(iso: string | null): string {
  return iso ? formatDateTime(iso, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
}

export interface BrandKitCopy {
  checked: boolean;
  /** The visible state line. */
  line: string;
  /** A note under it, when the app waits for something. */
  note: string | null;
  /** Why the switch cannot move; the switch is disabled exactly when this is set. */
  blocked: string | null;
  /** The kit whose colours, fonts and logo the card shows. */
  sample: BrandKitSummary | null;
  /** Offer "Take the current kit". */
  offersTake: boolean;
}

/** The note under an "on" card: a refresh waiting for a publish, or a kit that moved on since. */
function onNote(view: AppBrandKitView): string | null {
  if (view.refresh) return 'Takes your current kit at the next publish. Asked in the building session.';
  if (view.changedSince) return `Your brand kit changed since ${takenDate(view.at)}.`;
  return null;
}

/** What the card says for one state. Pure, so every state is tested without a DOM. */
export function brandKitCopy(view: AppBrandKitView): BrandKitCopy {
  const current = view.current;
  const noKit = !view.available
    ? 'Your brand kit could not be read right now. Try again in a moment.'
    : 'You don’t have a brand kit yet. Set one up in Settings, under Design.';
  if (view.state === 'on' && view.taken) {
    return {
      checked: true,
      line: `On. ${kitName(view.taken)}, taken ${takenDate(view.at)}. Later changes to your kit don’t change this app.`,
      note: onNote(view),
      blocked: null,
      sample: view.taken,
      offersTake: !!current,
    };
  }
  if (view.state === 'pending') {
    if (!view.published) {
      return {
        checked: true,
        line: current
          ? `Takes ${kitName(current)} when you first publish. Until then the preview shows your current kit.`
          : 'Takes your brand kit when you first publish.',
        note: current ? null : noKit,
        blocked: null,
        sample: current,
        offersTake: false,
      };
    }
    // Published while the kit could not be read: the app shows the house look until a publish takes it.
    return {
      checked: true,
      line: 'Takes your brand kit at the next publish. Until then the app uses the EDDY house look.',
      note: current ? null : noKit,
      blocked: null,
      sample: current,
      offersTake: !!current,
    };
  }
  if (view.state === 'off') {
    return {
      checked: false,
      line: 'Off. The app uses the EDDY house look.',
      note: null,
      blocked: current ? null : noKit,
      sample: null,
      offersTake: false,
    };
  }
  return {
    checked: false,
    line: current ? `Not used. Turn it on to take ${kitName(current)} now.` : 'Not used. The app uses the EDDY house look.',
    note: null,
    blocked: current ? null : noKit,
    sample: null,
    offersTake: false,
  };
}

/** The kit accent the Accent field falls back to, or null when the app wears no kit. */
export function inheritedAccent(view: AppBrandKitView | undefined): string | null {
  if (!view) return null;
  if (view.state === 'on') return view.taken?.accent ?? null;
  // A published app still waiting wears the house look; before the first publish the preview shows the current kit.
  if (view.state === 'pending') return view.published ? null : (view.current?.accent ?? null);
  return null;
}

export function useAppBrandKit(slug: string, enabled = true) {
  return useQuery({
    queryKey: brandKitQueryKey(slug),
    queryFn: () => api.get<{ brandKit: AppBrandKitView }>(`/api/admin/apps/${slug}/brand-kit`).then((r) => r.brandKit),
    enabled,
  });
}

function KitSample({ kit }: { kit: BrandKitSummary }) {
  const fonts = [kit.display && `${kit.display} headings`, kit.body && `${kit.body} text`].filter(Boolean).join(', ');
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border pt-2.5 text-xs text-muted-foreground">
      {kit.accent && (
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-4 rounded-[5px] outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
            style={{ background: kit.accent }}
          />
          <span className="font-mono tabular-nums">{kit.accent}</span>
        </span>
      )}
      {fonts && <span>{fonts}</span>}
      {kit.hasLogo && <span className="sm:ml-auto">Logo included</span>}
    </div>
  );
}

export function BrandKitControl({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data: view, isPending, isError } = useAppBrandKit(slug);
  const save = useMutation({
    mutationFn: (use: boolean) =>
      api.put<{ brandKit: AppBrandKitView }>(`/api/admin/apps/${slug}/brand-kit`, { use }).then((r) => r.brandKit),
    onSuccess: (next, use) => {
      qc.setQueryData(brandKitQueryKey(slug), next);
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
      onSaved();
      toast.success(use ? 'Brand kit on. The app took your current kit.' : 'Brand kit off. The app uses the EDDY house look.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending) return <Skeleton className="h-20 w-full" />;
  if (isError || !view) {
    return <p className="text-xs text-muted-foreground">The brand kit setting could not be loaded.</p>;
  }
  const copy = brandKitCopy(view);
  const disabled = !!copy.blocked || save.isPending;

  return (
    <section aria-labelledby="app-brand-kit-title" className="rounded-lg border border-border bg-muted/30 p-3 space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <label id="app-brand-kit-title" htmlFor="app-brand-kit-switch" className="text-sm font-medium cursor-pointer">
            Use brand kit
          </label>
          <p className="text-xs text-muted-foreground text-pretty">{copy.line}</p>
        </div>
        <Switch
          id="app-brand-kit-switch"
          checked={copy.checked}
          disabled={disabled}
          onCheckedChange={(next) => save.mutate(next)}
          className="relative shrink-0 active:scale-[0.96] transition-[background-color,scale] before:absolute before:-inset-y-2.5 before:-inset-x-1 before:content-['']"
        />
      </div>
      {copy.sample && <KitSample kit={copy.sample} />}
      {copy.note && (
        <p className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-foreground text-pretty">{copy.note}</p>
      )}
      {copy.blocked && (
        <p className="text-xs text-muted-foreground text-pretty">
          {copy.blocked}{' '}
          {view.available && (
            <Link to={settingsPath('design')} className="underline underline-offset-2 hover:text-foreground">
              Open Design
            </Link>
          )}
        </p>
      )}
      {copy.offersTake && (
        <Button
          size="sm"
          variant="outline"
          disabled={save.isPending}
          onClick={() => save.mutate(true)}
          className="active:scale-[0.96] transition-[background-color,scale]"
        >
          Take the current kit
        </Button>
      )}
    </section>
  );
}
